import React, { useState, useContext, useRef, useCallback } from "react";
import { CartContext } from "../pages/CartPage/CartContext";

/* ─── Icons for food categories ─── */
const categoryIcons = [
  { id: "all", label: "All Items", emoji: "🍽️" },
  { id: "coffee", label: "Coffee & Drinks", emoji: "☕" },
  { id: "snacks", label: "Snacks", emoji: "🍿" },
  { id: "meals", label: "Meals", emoji: "🍛" },
  { id: "desserts", label: "Desserts", emoji: "🍰" },
  { id: "spices", label: "Spices & Herbs", emoji: "🌿" },
  { id: "organic", label: "Organic", emoji: "🥬" },
];

const filterTabs = ["Featured", "Best Sellers", "Popular"];

/* ─── Helpers ─── */
const toNumberOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const FoodThemeSection = ({ products = [], navigate }) => {
  const { addToCart } = useContext(CartContext);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [activeFilter, setActiveFilter] = useState("Featured");
  const [animatedButtonId, setAnimatedButtonId] = useState(null);
  const [flyingItem, setFlyingItem] = useState(null);
  const scrollRef = useRef(null);

  // Simple pseudo-filter (since server doesn't have sub-categories for food)
  const filteredProducts = products.filter(() => {
    // All items for now — category filtering can be enhanced when sub-categories exist
    return true;
  });

  // Shuffle for variation on different "filter" tabs
  const displayProducts = (() => {
    const items = [...filteredProducts];
    if (activeFilter === "Best Sellers") {
      // Sort by price descending as proxy
      items.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    } else if (activeFilter === "Popular") {
      // Reverse order as proxy
      items.reverse();
    }
    return items;
  })();

  // Deals: products with MRP discount
  const dealProducts = products.filter((p) => {
    const mrp = Number(p.mrp || 0);
    const price = Number(p.price || 0);
    return mrp > 0 && mrp > price;
  });

  const handleAddToCart = useCallback((product, event) => {
    const productId = product._id || product.id;
    const cartProduct = {
      id: productId,
      name: product.name,
      price: Number(product.price || 0),
      image: product.image || product.imageUrl || "",
      description: product.description || "",
      categoryId: product.categoryId || product.category || "",
      checkinDT: !!product.checkinDT,
      multiDayDiscountDays: Number(product.multiDayDiscountDays || 0),
      multiDayDiscountPercent: Number(product.multiDayDiscountPercent || 0),
      quantity: 1,
    };
    addToCart(cartProduct);
    setAnimatedButtonId(productId);
    setTimeout(() => setAnimatedButtonId(null), 300);

    // Flying animation
    const buttonRect = event.target.getBoundingClientRect();
    const cartIcons = document.querySelectorAll("[data-cart-icon]");
    let cartIcon = null;
    cartIcons.forEach((icon) => {
      const rect = icon.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) cartIcon = icon;
    });
    if (cartIcon) {
      const cartRect = cartIcon.getBoundingClientRect();
      setFlyingItem({
        id: Date.now(),
        startX: buttonRect.left + buttonRect.width / 2,
        startY: buttonRect.top + buttonRect.height / 2,
        deltaX: cartRect.left + cartRect.width / 2 - (buttonRect.left + buttonRect.width / 2),
        deltaY: cartRect.top + cartRect.height / 2 - (buttonRect.top + buttonRect.height / 2),
      });
      setTimeout(() => setFlyingItem(null), 800);
    }
  }, [addToCart]);

  const goToDetail = (product) => {
    if (typeof navigate === "function") navigate("productDetail", product._id || product.id);
  };

  const scrollCategories = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 160, behavior: "smooth" });
  };

  return (
    <div className="food-theme-section">
      {/* ────── TOP CATEGORIES ────── */}
      <section className="container mx-auto px-4 mt-8 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="text-xs font-bold text-green-600 uppercase tracking-wider">Categories</span>
            <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 mt-0.5">Top Categories</h2>
          </div>
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => scrollCategories(-1)}
              className="w-8 h-8 rounded-full border-2 border-green-500 text-green-600 flex items-center justify-center hover:bg-green-500 hover:text-white transition-all"
            >
              ‹
            </button>
            <button
              onClick={() => scrollCategories(1)}
              className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600 transition-all"
            >
              ›
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-3 md:gap-4 overflow-x-auto pb-2 scrollbar-hide"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {categoryIcons.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`flex flex-col items-center flex-shrink-0 transition-all duration-300 ${selectedCategory === cat.id ? "scale-105" : ""}`}
            >
              <div
                className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-2xl md:text-3xl transition-all duration-300 shadow-md ${selectedCategory === cat.id
                  ? "bg-gradient-to-br from-green-400 to-green-600 shadow-green-200 shadow-lg ring-4 ring-green-100"
                  : "bg-white hover:shadow-lg border border-gray-100"
                }`}
              >
                {cat.emoji}
              </div>
              <span
                className={`mt-1.5 text-[10px] md:text-xs font-semibold text-center max-w-[60px] md:max-w-[80px] leading-tight ${selectedCategory === cat.id ? "text-green-700" : "text-gray-600"}`}
              >
                {cat.label}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS ────── */}
      <section className="container mx-auto px-4 mb-8 mt-12 md:mt-16">
        <div className="flex items-center justify-between mb-1">
          <div>
            <span className="text-xs font-bold text-green-600 uppercase tracking-wider">Categories</span>
            <h2 className="text-2xl font-extrabold text-gray-900 mt-0.5">Featured Products</h2>
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-full p-1">
            {filterTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveFilter(tab)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                  activeFilter === tab
                    ? "bg-green-500 text-white shadow-md"
                    : "text-gray-600 hover:text-green-600"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-6 relative">
          {displayProducts.slice(0, 8).map((product) => {
            const price = Number(product.price || 0);
            const mrp = Number(product.mrp || 0);
            const showMrp = mrp > 0 && mrp > price;
            const discountPercent = showMrp ? Math.round(((mrp - price) / mrp) * 100) : 0;
            const productId = product._id || product.id;
            const calories = toNumberOrNull(product.calories);
            const preparationTime = toNumberOrNull(product.preparationTime);
            const protein = toNumberOrNull(product.protein);
            const metaParts = [];
            if (preparationTime != null) metaParts.push(`${preparationTime}m`);
            if (calories != null) metaParts.push(`${calories}cal`);
            if (protein != null) metaParts.push(`${protein}gP`);

            return (
              <div
                key={productId}
                className="group bg-white rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 overflow-visible border border-gray-50 hover:border-green-100 hover:z-20 relative"
              >
                {/* Image */}
                <div
                  className="relative h-48 overflow-hidden cursor-pointer bg-gradient-to-br from-green-50 to-emerald-50 rounded-t-2xl"
                  onClick={() => goToDetail(product)}
                >
                  {showMrp && discountPercent > 0 && (
                    <div className="absolute top-3 left-3 z-10 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-md">
                      {discountPercent}% OFF
                    </div>
                  )}
                  {product.image || product.imageUrl ? (
                    <img
                      src={product.image || product.imageUrl}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://placehold.co/400x300/E8F5E9/2E7D32?text=${(product.name || "Food").replace(/\s/g, "+")}`;
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-5xl">🍽️</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3
                    className="font-bold text-gray-900 text-sm mb-1 line-clamp-1 cursor-pointer hover:text-green-600 transition-colors"
                    onClick={() => goToDetail(product)}
                  >
                    {product.name}
                  </h3>
                  <p className="text-xs text-gray-500 mb-2 line-clamp-2">{product.description}</p>

                  {metaParts.length > 0 && (
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {metaParts.map((m, i) => (
                        <span key={i} className="text-[10px] bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">
                          {m}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Rating placeholder */}
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-1">4.5</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-end gap-2">
                      {showMrp && (
                        <span className="text-sm text-gray-400 line-through">₹{mrp.toFixed(0)}</span>
                      )}
                      <span className="text-lg font-extrabold text-green-600">₹{price.toFixed(0)}</span>
                    </div>

                    <button
                      onClick={(e) => handleAddToCart(product, e)}
                      className={`bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-md hover:shadow-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center gap-1 flex-shrink-0 ${
                        animatedButtonId === productId ? "scale-110" : ""
                      }`}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                      Add
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ────── PROMOTIONAL BANNERS ────── */}
      <section className="container mx-auto px-4 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Banner 1 */}
          <div className="relative overflow-hidden rounded-2xl h-52 bg-gradient-to-br from-green-50 via-green-100 to-emerald-100 shadow-md group">
            <div className="absolute inset-0 p-6 flex flex-col justify-center z-10">
              <span className="text-xs text-green-600 font-bold uppercase tracking-wider mb-1">Enjoy as it still</span>
              <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">Fresh Vegetable</h3>
              <button className="w-fit bg-gradient-to-r from-green-500 to-green-600 text-white px-5 py-2 rounded-full text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-2">
                Shop Now
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
            <div className="absolute right-0 bottom-0 w-2/5 h-full opacity-30 group-hover:opacity-50 transition-opacity">
              <img
                src="https://images.unsplash.com/photo-1540420773420-3366772f4999?w=400"
                alt="Fresh Vegetables"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Banner 2 */}
          <div className="relative overflow-hidden rounded-2xl h-52 shadow-md group">
            <img
              src="https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=800"
              alt="Organic Products"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
            <div className="absolute inset-0 p-6 flex flex-col justify-center z-10">
              <span className="text-xs text-green-300 font-bold uppercase tracking-wider mb-1">Best for you & health</span>
              <h3 className="text-xl md:text-2xl font-extrabold text-white mb-3 leading-tight">
                All Tasted Organic &<br />Fresh Products
              </h3>
              <button className="w-fit bg-gradient-to-r from-green-500 to-green-600 text-white px-5 py-2 rounded-full text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-2">
                Shop Now
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ────── DEAL OF THE WEEK ────── */}
      {dealProducts.length > 0 && (
        <section className="container mx-auto px-4 mb-12">
          <div className="text-center mb-6">
            <span className="text-xs font-bold text-green-600 uppercase tracking-wider">Best Deal</span>
            <h2 className="text-3xl font-extrabold text-gray-900 mt-1">Deal Of The Week</h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 relative">
            {dealProducts.slice(0, 4).map((product) => {
              const price = Number(product.price || 0);
              const mrp = Number(product.mrp || 0);
              const productId = product._id || product.id;

              return (
                <div
                  key={productId}
                  className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-50 hover:border-green-100 text-center group hover:z-20 relative overflow-visible"
                >
                  <div
                    className="h-32 mb-3 overflow-hidden rounded-xl cursor-pointer bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center"
                    onClick={() => goToDetail(product)}
                  >
                    {product.image || product.imageUrl ? (
                      <img
                        src={product.image || product.imageUrl}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://placehold.co/300x200/E8F5E9/2E7D32?text=${(product.name || "Deal").replace(/\s/g, "+")}`;
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-4xl">🏷️</span>
                      </div>
                    )}
                  </div>

                  <h4 className="text-sm font-bold text-gray-800 mb-1 line-clamp-1">{product.name}</h4>

                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-xs text-gray-400 line-through">₹{mrp.toFixed(0)}</span>
                    <span className="text-base font-extrabold text-green-600">₹{price.toFixed(0)}</span>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center justify-center gap-0.5 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-3 h-3 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-[10px] text-gray-400 ml-1">4.5</span>
                  </div>

                  <button
                    onClick={(e) => handleAddToCart(product, e)}
                    className={`w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-2 rounded-full text-xs font-bold shadow-md hover:shadow-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center justify-center gap-1.5 ${
                      animatedButtonId === productId ? "scale-105" : ""
                    }`}
                  >
                    Shop Now
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Flying cart animation */}
      {flyingItem && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            left: flyingItem.startX,
            top: flyingItem.startY,
            transform: `translate(${flyingItem.deltaX}px, ${flyingItem.deltaY}px)`,
            animation: "flyToCartFood 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards",
          }}
        >
          <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center text-xl shadow-lg">
            🛒
          </div>
        </div>
      )}

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes flyToCartFood {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          50% { opacity: 0.8; }
          100% { transform: translate(var(--delta-x), var(--delta-y)) scale(0.3); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default FoodThemeSection;
